import { useCallback } from 'react'
import { useMutation, ApolloError } from '@apollo/client'

import { MUTATION_UPDATE_TASK_PROGRESS } from '@libs/apollo/mutations'

import { TASK_WITH_COMMENTS_AND_ACTIVITIES } from '@libs/apollo/queries'

import { Task, TaskProgressUpdateInput, MutationUpdateTaskProgressArgs } from '@types'

interface Data {
  updateTaskProgress: Task
}

interface UseUpdateProgressTaskProps {
  onCompleted?: () => void
}

export const useUpdateTaskProgress = (
  props?: UseUpdateProgressTaskProps,
): {
  updateTaskProgress: (input: TaskProgressUpdateInput) => Promise<void>
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const [updateTaskProgress, { loading, error, data }] = useMutation<Data, MutationUpdateTaskProgressArgs>(
    MUTATION_UPDATE_TASK_PROGRESS,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const _updateTaskProgress = useCallback(
    async (input: TaskProgressUpdateInput): Promise<void> => {
      await updateTaskProgress({
        variables: { input },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: TASK_WITH_COMMENTS_AND_ACTIVITIES,
            variables: {
              id: input.taskId,
            },
          },
        ],
        onCompleted: props?.onCompleted,
        update(cache, { data }) {
          if (data && data.updateTaskProgress) {
            cache.modify({
              id: cache.identify({ id: input.taskId, __typename: 'Task' }),
              fields: {
                status: () => data.updateTaskProgress.status,
                progress: () => data.updateTaskProgress.progress,
              },
            })
          }
        },
      })
    },
    [props?.onCompleted, updateTaskProgress],
  )

  return { updateTaskProgress: _updateTaskProgress, loading, error, data }
}
